import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, CreditCard, Smartphone, Banknote, Copy, Check, ExternalLink, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Holly's manual payment handles
const VENMO_HANDLE = "aviana_yoga";
const VENMO_DEEP_LINK = "https://venmo.com/aviana_yoga";
const ZELLE_EMAIL = "avianayoga@gmail.com";

// Stripe pass-through fee. Stripe's standard US card rate is 2.9% + $0.30.
// We add a transparent "Card processing fee" line at checkout so students see
// exactly what's added when paying by card vs. paying by Venmo/Zelle.
const STRIPE_PCT = 0.029;
const STRIPE_FIXED = 0.30;
function calcCardFee(usd) {
  return Math.round((usd * STRIPE_PCT + STRIPE_FIXED) * 100) / 100;
}

export default function PaymentSelect() {
  const { regId } = useParams();
  const navigate = useNavigate();
  const [reg, setReg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/trainings/registrations/${regId}`);
        setReg(data);
      } catch {
        toast.error("Registration not found.");
        navigate("/");
      }
    })();
  }, [regId, navigate]);

  if (!reg) return <div className="pt-40 container-aviana text-aviana-muted">Loading…</div>;

  const amount = reg.amount_today_usd;
  const cardFee = calcCardFee(amount);
  const cardTotal = Math.round((amount + cardFee) * 100) / 100;

  const choose = async (method) => {
    setBusy(true);
    try {
      if (method === "card") {
        // Live Stripe Checkout — backend computes the amount + fee server-side.
        const { data } = await api.post(`/trainings/registrations/${regId}/stripe-checkout`, {
          origin_url: window.location.origin,
        });
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
        throw new Error("No checkout URL returned");
      }
      // Manual methods: just record the choice and route to the manual-payment page.
      await api.post(`/trainings/registrations/${regId}/choose-payment`, { method });
      navigate(`/payment/${regId}/manual?method=${method}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
    toast.success("Copied");
  };

  return (
    <div className="pt-28 pb-24 bg-aviana-paper" data-testid="payment-select-page">
      <div className="container-aviana max-w-3xl">
        <Link to={`/register/${reg.offering_slug}`} className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-8">
          <ArrowLeft size={14} /> Back to registration
        </Link>

        {/* Success banner */}
        <div className="bg-aviana-cream border border-aviana-gold/40 rounded-2xl p-6 mb-10 flex items-start gap-4" data-testid="reg-confirmation">
          <Sparkles size={20} className="text-aviana-gold mt-1 flex-shrink-0" />
          <div>
            <p className="font-display text-2xl text-aviana-ink leading-tight">Beautiful, {reg.full_name.split(" ")[0]} — your registration is saved.</p>
            <p className="mt-2 text-aviana-inkSoft text-[14px] leading-[1.7]">Last step: choose how you'd like to pay your <strong className="text-aviana-plum">${amount.toLocaleString()} {reg.payment_type === "deposit" ? "deposit" : "tuition in full"}</strong> for <em>{reg.offering_title}</em>.</p>
            {reg.balance_due_usd > 0 && (
              <p className="mt-1.5 text-aviana-muted text-[13px]">Remaining balance after deposit: ${reg.balance_due_usd.toLocaleString()} — due {reg.balance_due_label ? `by ${reg.balance_due_label}` : "before it begins"}.</p>
            )}
          </div>
        </div>

        <p className="overline-plum mb-3">Choose your payment method</p>
        <h1 className="font-display text-4xl text-aviana-ink leading-tight">Three ways to pay.</h1>
        <p className="mt-3 text-aviana-muted text-[15px]">Card includes a small processing fee. Venmo & Zelle are free for both of us — and the spot is held the same way once Holly confirms funds.</p>

        <div className="mt-10 space-y-5">
          {/* Card */}
          <button
            onClick={() => choose("card")}
            disabled={busy}
            className="w-full text-left bg-white border border-aviana-border hover:border-aviana-plum/50 rounded-2xl p-7 transition-all flex items-start gap-5 group"
            data-testid="pay-card"
          >
            <div className="w-12 h-12 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
              <CreditCard size={20} />
            </div>
            <div className="flex-1">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <p className="font-display text-2xl text-aviana-ink">Card · Stripe</p>
                <p className="font-display text-2xl text-aviana-plum">${cardTotal.toFixed(2)}</p>
              </div>
              <p className="mt-1 text-aviana-muted text-[13px]">${amount.toLocaleString()} + ${cardFee.toFixed(2)} card processing fee · Secure checkout · Automated balance reminders</p>
            </div>
          </button>

          {/* Venmo */}
          <div className="bg-white border border-aviana-border rounded-2xl p-7" data-testid="pay-venmo">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
                <Smartphone size={20} />
              </div>
              <div className="flex-1">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <p className="font-display text-2xl text-aviana-ink">Venmo</p>
                  <p className="font-display text-2xl text-aviana-ink">${amount.toLocaleString()}</p>
                </div>
                <p className="mt-1 text-aviana-muted text-[13px]">No processing fee. Send to <strong className="text-aviana-plum">@{VENMO_HANDLE}</strong> with your full name in the note.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <a href={VENMO_DEEP_LINK} target="_blank" rel="noopener noreferrer" className="btn-secondary !py-2 !px-4 !text-[11px] inline-flex items-center gap-1.5" data-testid="open-venmo">
                    Open Venmo <ExternalLink size={11} />
                  </a>
                  <button onClick={() => copy(VENMO_HANDLE, "venmo")} className="btn-secondary !py-2 !px-4 !text-[11px] inline-flex items-center gap-1.5">
                    {copied === "venmo" ? <Check size={11} /> : <Copy size={11} />}
                    {copied === "venmo" ? "Copied" : `@${VENMO_HANDLE}`}
                  </button>
                  <button
                    onClick={() => choose("venmo")}
                    disabled={busy}
                    className="btn-primary !py-2 !px-5 !text-[11px]"
                    data-testid="confirm-venmo"
                  >
                    I'll pay via Venmo →
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Zelle */}
          <div className="bg-white border border-aviana-border rounded-2xl p-7" data-testid="pay-zelle">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
                <Banknote size={20} />
              </div>
              <div className="flex-1">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <p className="font-display text-2xl text-aviana-ink">Zelle</p>
                  <p className="font-display text-2xl text-aviana-ink">${amount.toLocaleString()}</p>
                </div>
                <p className="mt-1 text-aviana-muted text-[13px]">No processing fee. Send to <strong className="text-aviana-plum">{ZELLE_EMAIL}</strong> through your banking app.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => copy(ZELLE_EMAIL, "zelle")} className="btn-secondary !py-2 !px-4 !text-[11px] inline-flex items-center gap-1.5">
                    {copied === "zelle" ? <Check size={11} /> : <Copy size={11} />}
                    {copied === "zelle" ? "Copied" : ZELLE_EMAIL}
                  </button>
                  <button
                    onClick={() => choose("zelle")}
                    disabled={busy}
                    className="btn-primary !py-2 !px-5 !text-[11px]"
                    data-testid="confirm-zelle"
                  >
                    I'll pay via Zelle →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-[12px] text-aviana-muted mt-10">
          Questions? Email Holly at <a href="mailto:holly@avianayoga.com" className="text-aviana-plum underline underline-offset-2">holly@avianayoga.com</a>.
        </p>
      </div>
    </div>
  );
}
